import React, { useState, useEffect } from 'react';
import { Modal, Steps, Button } from 'antd';
import {
  SmileOutlined, BuildOutlined, FileTextOutlined, AimOutlined,
  BarChartOutlined, SearchOutlined,
} from '@ant-design/icons';

// Bump this when the guide content meaningfully changes so returning users see it again.
export const ONBOARDING_VERSION = 1;
export const onboardingKey = (userId) => `sg_onboarding_done_v${ONBOARDING_VERSION}_${userId}`;

const TUTOR_STEPS = [
  {
    icon: <SmileOutlined />,
    title: 'Welcome to SGLearner 👋',
    body: "Let's get you set up to guide your tutees. Here's the quick tour for tutors — it takes about a minute.",
  },
  {
    icon: <BuildOutlined />,
    title: '1. Build your Roadmap',
    body: "Head to Roadmap Builder to set up your roadmap templates. We've already created MOE templates for you, but you're free to build your own if you'd like.",
    cta: { label: 'Open Roadmap Builder', to: '/stage-gate/builder' },
  },
  {
    icon: <FileTextOutlined />,
    title: '2. Upload your Materials',
    body: 'Go to Materials to upload any notes, worksheets or documents you want to share with your tutees.',
    cta: { label: 'Open Materials', to: '/material' },
  },
  {
    icon: <AimOutlined />,
    title: '3. Assign a Roadmap',
    body: 'After a tutee subscribes to you, open Roadmap to assign one of your templates. Unlock a stage whenever you want to let them have a crack at it.',
    cta: { label: 'Open Roadmap', to: '/stage-gate' },
  },
  {
    icon: <BarChartOutlined />,
    title: '4. Stay on top of things',
    body: 'Your Dashboard flags anything outstanding, and the Analytics page shows how well your students are faring.',
    cta: { label: 'Go to Dashboard', to: '/dashboard' },
  },
];

const STUDENT_STEPS = [
  {
    icon: <SmileOutlined />,
    title: 'Welcome to SGLearner 👋',
    body: "Here's how to get started as a student — it only takes a minute.",
  },
  {
    icon: <SearchOutlined />,
    title: '1. Find a tutor',
    body: 'Browse Find Tutors and subscribe to whoever you like. Prices are shown inside, but everything is completely free right now!',
    cta: { label: 'Find Tutors', to: '/search' },
  },
  {
    icon: <AimOutlined />,
    title: '2. Work through your Stages',
    body: "Once you've subscribed, open Roadmap to see which stages you need to complete for the PSLE syllabus. 🎯",
    cta: { label: 'Open Roadmap', to: '/stage-gate' },
  },
];

export default function OnboardingGuide({ open, role, onClose, navigate }) {
  const steps = role === 'student' ? STUDENT_STEPS : TUTOR_STEPS;
  const [current, setCurrent] = useState(0);

  // Restart from the beginning each time the guide is opened.
  useEffect(() => { if (open) setCurrent(0); }, [open]);

  const isLast = current === steps.length - 1;
  const step   = steps[current];

  const goTo = (to) => { onClose(); navigate(to); };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
      centered
      maskClosable={false}
    >
      <div style={{ padding: '8px 4px' }}>
        <Steps
          current={current}
          size="small"
          items={steps.map(() => ({}))}
          style={{ marginBottom: 28 }}
        />

        <div style={{ textAlign: 'center', minHeight: 180 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', background: '#f5f5f5',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, color: '#0a0a0a', marginBottom: 16,
          }}>
            {step.icon}
          </div>
          <h2 style={{ fontSize: 19, fontWeight: 700, color: '#0a0a0a', margin: '0 0 10px' }}>
            {step.title}
          </h2>
          <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, maxWidth: 420, margin: '0 auto' }}>
            {step.body}
          </p>
          {step.cta && (
            <Button
              type="link"
              onClick={() => goTo(step.cta.to)}
              style={{ padding: 0, marginTop: 14, fontWeight: 600, color: '#0a0a0a' }}
            >
              {step.cta.label} →
            </Button>
          )}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0f0',
        }}>
          <Button type="text" onClick={onClose} style={{ color: '#999', fontSize: 13 }}>
            Skip
          </Button>
          <div style={{ display: 'flex', gap: 8 }}>
            {current > 0 && (
              <Button onClick={() => setCurrent(c => c - 1)}>Back</Button>
            )}
            {isLast ? (
              <Button type="primary" onClick={onClose}
                style={{ background: '#0a0a0a', border: 'none' }}>
                Got it
              </Button>
            ) : (
              <Button type="primary" onClick={() => setCurrent(c => c + 1)}
                style={{ background: '#0a0a0a', border: 'none' }}>
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
